import { formatInteger, pluralize } from '@monetize/utils/core';
import {
  FiChevronLeft,
  FiChevronRight,
  FiChevronsLeft,
  FiChevronsRight,
} from 'react-icons/fi';
import { MBox, MFlex } from '../chakra';
import MButton from '../MButton';
import { MCustomSelect } from '../MCustomSelect/MCustomSelect';
import MIconButton from '../MIconButton';
import MText from '../MText';
import { AppTable } from './appTable';

interface DataTablePaginationProps<TData extends object> {
  table: AppTable<TData>;
  /**
   * Total number of elements across all pages (for server-side pagination)
   * If not provided, will use filtered row count (client-side)
   */
  totalElements?: number;
  /**
   * True while a page fetch is in flight. Disables page navigation so rapid
   * clicks can't fire multiple page changes before the previous one settles.
   */
  isLoading?: boolean;
}

export function DataTablePagination<TData extends object>({
  table,
  totalElements,
  isLoading,
}: DataTablePaginationProps<TData>) {
  const { pageIndex, pageSize } = table.store.state.pagination;
  /**
   * `getFilteredRowModel()` is safe here only because `columnFilteringFeature`
   * and the `filteredRowModel` slot are both registered in `appTable.ts` - v9
   * omits the method entirely when they are not.
   *
   * In server-controlled mode `manualFiltering` short-circuits it to the core
   * row model, so this is the unfiltered *current page* count. That is
   * pre-existing behavior and normally masked because `totalElements` is
   * supplied; in client-controlled mode it is the genuinely filtered count,
   * which is what the range label wants.
   */
  const totalRows = totalElements ?? table.getFilteredRowModel().rows.length;
  const pageCount = table.getPageCount();
  const canPreviousPage = table.getCanPreviousPage();
  const canNextPage = table.getCanNextPage();

  const startRow = pageIndex * pageSize + 1;
  const endRow = Math.min((pageIndex + 1) * pageSize, totalRows);

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxPagesToShow = 5;

    if (pageCount <= maxPagesToShow) {
      // Show all pages if total pages is less than max
      for (let i = 0; i < pageCount; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(0);

      let startPage = Math.max(1, pageIndex - 1);
      let endPage = Math.min(pageCount - 2, pageIndex + 1);

      // Adjust if we're near the beginning
      if (pageIndex <= 2) {
        endPage = 3;
      }

      // Adjust if we're near the end
      if (pageIndex >= pageCount - 3) {
        startPage = pageCount - 4;
      }

      // Add ellipsis before if needed
      if (startPage > 1) {
        pages.push('...');
      }

      // Add middle pages
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }

      // Add ellipsis after if needed
      if (endPage < pageCount - 2) {
        pages.push('...');
      }

      // Always show last page
      if (pageCount > 1) {
        pages.push(pageCount - 1);
      }
    }

    return pages;
  };

  if (totalRows === 0) {
    return null;
  }

  const pageNumbers = getPageNumbers();

  return (
    <MFlex
      justifyContent="space-between"
      alignItems="center"
      p={4}
      flexWrap="wrap"
      gap={2}
      width="100%"
    >
      {/* Left side: Rows info and page size selector */}
      <MFlex alignItems="center" gap={4}>
        <MText fontSize="sm" color="tPurple.dark">
          {pageCount <= 1
            ? `${formatInteger(totalRows)} ${pluralize('result', totalRows)}`
            : `Showing ${formatInteger(startRow)} to ${formatInteger(endRow)} of ${formatInteger(totalRows)} ${pluralize('result', totalRows)}`}
        </MText>
      </MFlex>

      {/* Center: Page navigation */}
      {pageNumbers.length > 1 ? (
        <MFlex alignItems="center" gap={1}>
          <MIconButton
            aria-label="First page"
            icon={<FiChevronsLeft />}
            onClick={() => table.setPageIndex(0)}
            isDisabled={!canPreviousPage || isLoading}
            size="sm"
            variant="ghost"
          />
          <MIconButton
            aria-label="Previous page"
            icon={<FiChevronLeft />}
            onClick={() => table.previousPage()}
            isDisabled={!canPreviousPage || isLoading}
            size="sm"
            variant="ghost"
          />

          {pageNumbers.map((page, index) => {
            if (page === '...') {
              return (
                <MText key={`ellipsis-${index}`} px={2} color="tPurple.dark">
                  ...
                </MText>
              );
            }

            const pageNum = page as number;
            const isCurrentPage = pageNum === pageIndex;

            return (
              <MButton
                key={pageNum}
                onClick={() => table.setPageIndex(pageNum)}
                isDisabled={isLoading}
                size="sm"
                fontWeight="normal"
                variant={isCurrentPage ? 'primary' : 'ghost'}
                minW="32px"
              >
                {pageNum + 1}
              </MButton>
            );
          })}

          <MIconButton
            aria-label="Next page"
            icon={<FiChevronRight />}
            onClick={() => table.nextPage()}
            isDisabled={!canNextPage || isLoading}
            size="sm"
            variant="ghost"
          />
          <MIconButton
            aria-label="Last page"
            icon={<FiChevronsRight />}
            onClick={() => table.setPageIndex(pageCount - 1)}
            isDisabled={!canNextPage || isLoading}
            size="sm"
            variant="ghost"
          />
        </MFlex>
      ) : (
        <MBox />
      )}

      {/* Right side: Page Size */}
      <MFlex alignItems="center" gap={2}>
        <MText fontSize="sm" color="tPurple.dark" whiteSpace="nowrap">
          Rows per page:
        </MText>
        <MCustomSelect
          size="sm"
          value={pageSize}
          onChange={(value) => {
            table.setPageSize(Number(value));
          }}
          width="80px"
          items={[20, 50, 100]}
        />
      </MFlex>
    </MFlex>
  );
}
